import { FC, useContext, useEffect, useState, useMemo, useRef, JSX } from "react";
import { Link, RouteComponentProps } from "react-router-dom";
import { toast } from "react-toastify";
import { Breadcrumb, BreadcrumbTemplateEnum, Breadcrumbs, Page, PageHeader, PageHeaderRight, PageInner, PageSection, sortByDate } from "@app/shared";
import { ColumnConfig, numberSort, SortDirections, Waffles, wafflesKeyColumnName, WafflesPaginationProps } from "@app/shared/waffles";
import { SortingMetadata } from "@app/shared/waffles/exports";
import { QESAlert, QESButton, QESTextInput } from "@app/shared/qes-standards";
import { ClientListItem } from "@app/client";
import { Roles, UserContext } from "@app/user";
import { AddTemplateModel, CreateEditPanelContent, TemplateCollectionMetadataModelWithActiveProjects, TemplateCollectionMetadataListModel, TemplateService, TemplateMetadataModel } from "@app/admin/manage-templates";
import { FlyoutLink } from "@app/shared/Flyouts";
import { ModalLink } from "@app/shared/Modals";
import { QesIcons } from "@app/shared/icons/qesIcons";
import UploadTemplateFlyout from "./UploadTemplateFlyout";
import TemplateHistoryFlyout from "./TemplateHistoryFlyout";
import moment from "moment";
import EditTemplateOptionsFlyout from "./EditTemplateOptionsFlyout";

interface TemplateCollectionTableData {
  templateCollectionName: JSX.Element;
  category: string;
  activeProjectCount: number;
  archived: JSX.Element;
  isActive: string;
  isSelected: boolean;
  templateCount: number;
  [wafflesKeyColumnName]: string;
}

interface TemplateTableData {
  activeProjectCount: number;
  archived: JSX.Element;
  lastUpdated: JSX.Element;
  templateId: string;
  templateName: JSX.Element;
  update: JSX.Element;
  [wafflesKeyColumnName]: string;
}

const columnConfig: ColumnConfig = {
  activeProjectCount: {
    displayText: "Active Projects",
    sizeContentToFit: true,
    testId: "templateCollection-activeProjectCount",
  },
  archived: {
    displayText: "Unarchive/Archive",
    sizeContentToFit: true,
    testId: "templateCollection-archived",
  },
  category: {
    displayText: "Template Category",
    sizeContentToFit: true,
    testId: "templateCollection-category",
  },
  isActive: {
    displayText: "Status",
    sizeContentToFit: true,
    testId: "templateCollection-isActive",
  },
  templateCollectionName: {
    displayText: "Template Collection Name",
    sizeContentToFit: true,
    testId: "templateCollection-templateCollectionName",
  },
  templateCount: {
    displayText: "Templates",
    sizeContentToFit: true,
    testId: "templateCollection-templateCount",
  },
  templateList: {
    displayText: "",
    sizeContentToFit: true,
    testId: "templateCollection-templateList",
  },
};

const templateColumnConfig: ColumnConfig = {
  activeProjectCount: {
    displayText: "Active Projects",
    sizeContentToFit: true,
    testId: "template-activeProjectCount",
  },
  archived: {
    displayText: "Archived/Unarchive",
    sizeContentToFit: true,
    testId: "template-archived",
  },
  lastUpdated: {
    displayText: "LastUpdated",
    sizeContentToFit: true,
    testId: "template-lastUpdated",
  },
  templateName: {
    displayText: "Template Name",
    sizeContentToFit: true,
    testId: "template-templateName",
  },
  update: {
    displayText: "Update",
    sizeContentToFit: true,
    testId: "template-publish",
  },
};

interface PageSettings {
  skip: number,
  sortDetails: SortingMetadata<TemplateCollectionTableData>,
}

interface TemplatesMatchProps {
  clientId?: string;
}

type TemplatesProps = RouteComponentProps<TemplatesMatchProps, undefined, { category: string }>;

export const Templates: FC<React.PropsWithChildren<TemplatesProps>> = (props: TemplatesProps) => {

  const templateService = new TemplateService();
  const user = useContext(UserContext);

  const [loading, setLoading] = useState(false);
  const [sidePanelLoading, setSidePanelLoading] = useState(false);
  const [selectedTemplateClientId, setSelectedTemplateClientId] = useState<number>(0);
  const [uploadSuccess, setUploadSuccess] = useState<boolean>(undefined);

  const [isQuestAdmin] = useState(user.roles.some(x => x === Roles.QUEST_ADMIN));
  const [templateCollections, setTemplateCollections] = useState<TemplateCollectionTableData[]>([]);
  const [selectedRow, setSelectedRow] = useState<TemplateCollectionMetadataModelWithActiveProjects>();
  const [templateList, setTemplateList] = useState<TemplateTableData[]>([]);
  const [take] = useState(50);
  const [totalRecords, setTotalRecords] = useState(0);
  const [numRecordsDisplayed, setNumRecordsDisplayed] = useState(0);
  const [templateNameFilter, setTemplateNameFilter] = useState("");
  const [pageSettings, setPageSettings] = useState<PageSettings>(
    {
      skip: 0,
      sortDetails: {
        sortDirection: SortDirections.None,
        sortedColumn: null,
      }
    });

  const firstRun = useRef(true);

  const paginationProps: WafflesPaginationProps = {
    changePage: (pageNumber: number) => {
      setPageSettings({ ...pageSettings, skip: (pageNumber - 1) * take })
    },
    recordsPerPage: take,
    skip: pageSettings.skip,
    totalRecords: templateCollections.length,
  }

  const breadcrumbs: (string | Breadcrumb)[] = useMemo(() => {
    const breadcrumbs: (string | Breadcrumb)[] = [];

    if (!!props.match.params.clientId) {
      breadcrumbs.push(
        {
          link: `/admin/client-administration/${props.match.params.clientId}`,
          text: !!props.location.state ? props.location.state.category : "Client",
        });
    }

    breadcrumbs.push("Manage Templates");

    return breadcrumbs;
  }, [props.match.params.clientId, props.location.state]);

  const fetchTemplates = async () => {
    setLoading(true);
    const templateCollectionList: TemplateCollectionMetadataListModel = { templateCollectionList: [], totalCount: 0 };

    setTotalRecords(templateCollectionList.totalCount);
    setNumRecordsDisplayed(templateCollectionList.templateCollectionList.length);

    firstRun.current = false;
    setLoading(false);
  }

  useEffect(() => {
    const fetchData = async () =>{
      const data = await createTemplateCollectionWafflesTable(undefined);
      setTemplateCollections(data);
      setTotalRecords(data.length);
    }

  fetchData();
},[pageSettings]);

  useEffect(() => {
    setNumRecordsDisplayed(templateCollections.slice(paginationProps.skip, paginationProps.skip + take).length + paginationProps.skip)
  }, [paginationProps]);

  useEffect(() => {  //sorts by unarchived with the data is loaded/reloaded
    customSortTemplateList(templateList, "archived", SortDirections.Desc)
  }, [templateList]);

  useEffect(() => { //sorts by unarchived with the data is loaded/reloaded if the sort is not already set
    if (!pageSettings.sortDetails.sortedColumn && templateCollections.length>0) {
      customSortTemplateCollection(templateCollections, "archived", SortDirections.Desc);
    }
  }, [templateCollections]);

  const archiveTemplateCollection  = (templateCollectionId: number) => {
    templateService.ArchiveTemplateCollection(templateCollectionId).then(() => {
      createTemplateCollectionWafflesTable(selectedRow);
    });
  }

  const unarchiveTemplateCollection  = (templateCollectionId: number) => {
    templateService.UnarchiveTemplateCollection(templateCollectionId).then(() => {
      createTemplateCollectionWafflesTable(selectedRow);
    });
  }

  useEffect(() => {
    createTemplateCollectionWafflesTable(selectedRow);
  }, [selectedTemplateClientId] );

  const archiveTemplate = (templateCollectionId: number, templateId: string) => {
    templateService.ArchiveTemplate(templateCollectionId,templateId).then(() => {
      createTemplateCollectionWafflesTable(selectedRow);
    });
  }

  const unarchiveTemplate  = (templateCollectionId: number, templateId: string) => {
    templateService.UnarchiveTemplate(templateCollectionId,templateId).then(() => {
      createTemplateCollectionWafflesTable(selectedRow);
    });
  }

  const createTemplateListWafflesTable = (data: TemplateMetadataModel[]): TemplateTableData[] => {
    return data.map((rec) => {
      /* eslint-disable sort-keys */
      const offset = new Date().getTimezoneOffset();
      const localizedDate = moment(rec.createdDate).subtract(offset, "minutes");
      return ({
        key: rec.templateId.toString(),
        templateName: (<Link className="qes-buttonqes-button qes-button-secondary" to={{ pathname: `/admin/manage-templates/manage-specialties/specialties/${rec.templateCollectionId}/${rec.templateId}` }} >{rec.templateName}</Link>),
        activeProjectCount: rec.activeProjects,
        lastUpdated: (
          <FlyoutLink
            content={{
              title: "Template History Timeline",
              body: <TemplateHistoryFlyout
                correlationId={rec.correlationId}
                templateCollectionId={rec.templateCollectionId}
              />
            }}
            classNames="qes-button-secondary"
            testId="mange-templates-list__template-history-flyout"
            size="20"
          >
            {localizedDate.format("MM/DD/YYYY h:mma")}
          </FlyoutLink>),
        update: (
          <FlyoutLink
            onClose={async () => { setTemplateList([]); setTemplateCollections(await createTemplateCollectionWafflesTable(selectedRow)) }}
            disabled={rec.archivedDate != undefined && selectedTemplateClientId !== 0 }
            content={{
              body: <UploadTemplateFlyout
                setUploadSuccess={setUploadSuccess}
                userId={user.userId}
                template={rec}
                onUpload={() => {
                  toast.success(<span>File successfully uploaded</span>, {
                    containerId: "z-index-9999",
                    type: "success"
                  });
                }}
                />,
              title: "Upload Template JSON",
            }}
            testId="manage-templates-list__update-template-flyout"
            classNames="qes-button-secondary"
          >
            Update
          </FlyoutLink>
        ),
        archived: (rec.archivedDate == undefined && selectedTemplateClientId !== 0 &&
          <ModalLink
            classNames="qes-button-secondary danger"
            content={{
              body: <p> {rec.activeProjects == 0 ? "Are you sure you want to archive this template?" : "Are you sure you want to archive this template? Projects using the template cannot be edited after archiving."}</p>,
              cancelButtonText: "Cancel",
              confirmButtonAction: () => archiveTemplate(rec.templateCollectionId,rec.templateId),
              confirmButtonText: "Archive",
              confirmButtonType: "primary-danger",
              title: `Archive Template`,
            }}
            testId="manage-templates-list__archive-modal"
            hideBodyScroll={true}
          >
            Archive
          </ModalLink>) ||
          (rec.archivedDate != undefined && selectedTemplateClientId !== 0 &&
            <ModalLink
              classNames="qes-button-secondary"
              content={{
                body: "Are you sure you want to unarchive this template?",
                cancelButtonText: "Cancel",
                confirmButtonAction: () => unarchiveTemplate(rec.templateCollectionId, rec.templateId),
                confirmButtonText: "Unarchive",
                confirmButtonType: "primary",
                title: `Unarchive Template`,
              }}
              testId="manage-templates-list__unarchive-modal"
              hideBodyScroll={true}
            >
              Unarchive
            </ModalLink>) ||
          (rec.archivedDate == undefined && selectedTemplateClientId === 0 &&
            <div>
              {"Unarchived"}
            </div>
          ) ||
          (rec.archivedDate != undefined && selectedTemplateClientId === 0 &&
            <div>
              {"Archived"}
            </div>
          ),
        templateId: rec.templateId,
      });
    });
    /* eslint-enable */
  }

  function translateResourceTypeEnum(enumValue: string) {
    if(enumValue == "adequacyTemplateCollection") {
      return "Adequacy Template Collection";
    }
    else if (enumValue == "adequacyZipTemplateCollection") {
      return "Adequacy Zip Template Collection";
    }
    return "Unknown Resouce Type";
  };

  const createTemplateCollectionWafflesTable = async (row: TemplateCollectionMetadataModelWithActiveProjects): Promise<TemplateCollectionTableData[]> => {
    /* eslint-disable */
    const data = await templateService.getAllTemplateCollections();

    let templateCollectionsData: TemplateCollectionMetadataModelWithActiveProjects[] = data;
    if (!!templateNameFilter) {
      templateCollectionsData = templateCollectionsData.filter(tc => tc.resource.name.toLowerCase().includes(templateNameFilter.toLowerCase()));
    }
    //reset current row that is selected to be unselected if there is one
    if(row){
      templateCollectionsData.find((td) => td.templateCollectionId == row.templateCollectionId).isSelected = true;
      var templateList = data.find((td) => td.templateCollectionId == row.templateCollectionId).templates;
      setSelectedRow(row);
      setSelectedTemplateClientId(row.resource.clientId);
      setTemplateList(createTemplateListWafflesTable(templateList));
    }

    const rowData = templateCollectionsData.map((rec) => {
      const currTemplates = rec.templates;
      var templateNames = ""
      {
        currTemplates.map((template) => (
          templateNames += (template.templateName + '\n\n')
        ))
      }

      return ({
        key: rec.templateCollectionId.toString(),
        templateCollectionName: (<QESButton
          qesType="secondary"
          qesTestId="select-templateCollection__select-button"
          onClick={() => createTemplateCollectionWafflesTable(rec)}
        >{rec.resource.name}
        </QESButton>),
        category: translateResourceTypeEnum(rec.resource.resourceType.toString()),
        activeProjectCount: rec.activeProjects,
        templateCount: rec.templates.length,
        isActive: rec.resource.resourceStatus.toString(),
        isSelected: rec.isSelected,
        archived: (
          (!rec.resource.isArchived &&
            <ModalLink
              classNames="qes-button-secondary danger"
              content={{
                body: (<>
                  <p> {rec.activeProjects == 0 ? "Are you sure you want to archive this template collection, the following templates will be archived:" : "Are you sure you want to archive this template collection, the templates below will be archived, and projects using the template cannot be edited after archiving."}
                    {currTemplates.map((template, index) => (
                      <>
                        <br />
                        <span key={index}>
                          {template.templateName}
                        </span>
                      </>
                    ))}
                  </p>
                </>),
                cancelButtonText: "Cancel",
                confirmButtonAction: () => archiveTemplateCollection(rec.templateCollectionId),
                confirmButtonText: "Archive",
                confirmButtonType: "primary-danger",
                title: `Archive Template Collection`,
              }}
              testId="manage-templates-list__archive-collection-modal"
              hideBodyScroll={true}
            >
              Archive
            </ModalLink>) ||
          (rec.resource.isArchived &&
            <ModalLink
              classNames="qes-button-secondary"
              content={{
                body: "Are you sure you want to unarchive this template collection?",
                cancelButtonText: "Cancel",
                confirmButtonAction: () => unarchiveTemplateCollection(rec.templateCollectionId),
                confirmButtonText: "Unarchive",
                confirmButtonType: "primary",
                title: `Unarchive Template Collection`,
              }}
              testId="manage-templates-list__unarchive-collection-modal"
              hideBodyScroll={true}
            >
              Unarchive
            </ModalLink>)
        ),
      });
      /* eslint-enable */
    });
    setTemplateCollections(rowData);
    return rowData;
  }

  const customSortTemplateCollection = (data: TemplateCollectionTableData[], newSortedColumn: keyof TemplateCollectionTableData, sortDirection: SortDirections) => {
    if (sortDirection === SortDirections.None) {
      return data;
    }
    const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;

    switch (newSortedColumn) {
      case "templateCollectionName":
      case "archived":
        return data.sort((a, b) => (a[newSortedColumn].props["children"] > b[newSortedColumn].props["children"] ? 1 : -1) * multiplier);
      case "category":
      case "isActive":
        return data.sort((a, b) => (a[newSortedColumn] > b[newSortedColumn] ? 1 : -1) * multiplier);
      case "activeProjectCount":
      case "templateCount":
        return numberSort(data, newSortedColumn, multiplier);
    }
    return data;
  }

  const customSortTemplateList = (data: TemplateTableData[], newSortedColumn: keyof TemplateTableData, sortDirection: SortDirections) => {
    if (sortDirection === SortDirections.None) {
      return data;
    }

    const multiplier = sortDirection === SortDirections.Asc ? 1 : -1;

    switch (newSortedColumn) {
      case "templateName":
      case "archived":
      case "update":
        return data.sort((a, b) => (a[newSortedColumn].props["children"] > b[newSortedColumn].props["children"] ? 1 : -1) * multiplier);
      case "activeProjectCount":
        return numberSort(data, newSortedColumn, multiplier);
      case "lastUpdated":
        return sortByDate(data, newSortedColumn, sortDirection, "templateName", "key");
    }
    return data;
  }

  const handleSearch = () => {
    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const handleSearchKeyUp = (e: React.KeyboardEvent) => {
    if (e.key.toLowerCase() === "enter") {
      handleSearch();
    }
  }

  const clearFilter = () => {
    setTemplateNameFilter("");
    setPageSettings({ ...pageSettings, skip: 0 });
  }

  const handleTemplateCreate = async (onClose: () => void, templateCollectionName: string, templateCount: number, isActive: boolean, clientId: number, editUsers: number[]) => {
    const createModel: AddTemplateModel = {
      clientId,
      editUserIds: editUsers,
      isActive,
      templateCollectionName,
      templateCount,
      userIds: []
    }

    setSidePanelLoading(true);
    templateService.addTemplate(createModel)
      .then(() => {
        onClose();
        setSidePanelLoading(false);
        fetchTemplates();
      }, () => {
        toast("An error occurred while creating this template.",
          {
            containerId: "z-index-9999",
            type: "error"
          }
        );
        onClose();
        setSidePanelLoading(false);
      }
      );
  }

  const rowDecorator = (row: TemplateCollectionTableData) => {
    return row.isSelected ? "info muted" : "";
  }

  return (
    <>
      <Page documentTitle="Manage Templates - Admin">
        <Breadcrumbs
          template={!!props.match.params.clientId ? BreadcrumbTemplateEnum.ClientAdministration : BreadcrumbTemplateEnum.Admin}
          items={breadcrumbs}
        />
        <PageInner>
          <PageHeader title="Manage Templates" iconName={QesIcons.FontAwesome.Folder}>
            <PageHeaderRight>
              <FlyoutLink
                content={{
                  body: <EditTemplateOptionsFlyout />  ,
                  title: "Edit Template Options",
                }}
                testId="manage-templates__edit-template-options-flyout"
                classNames="qes-button-primary"
                size="40"
              >
                Edit Template Options
              </FlyoutLink>
              <span style={{ width: '10px' }}> </span>
              <FlyoutLink
                content={{
                  body: <CreateEditPanelContent
                    currentUserid={user.userId}
                    defaultClient={{ clientId: user.clientId, name: user.clientName } as ClientListItem}
                    allowClientEdit={isQuestAdmin && !props.match.params.clientId}
                    handleSave={handleTemplateCreate}
                    loading={sidePanelLoading} />,
                  title: "Add a New Template",
                }}
                testId="manage-templates__create-group-flyout"
                classNames="qes-button-primary"
                size="40"
                disabled={true}
              >
                Create New Template
              </FlyoutLink>
            </PageHeaderRight>
          </PageHeader>
          <PageSection>
            <div className="manage-templates-filters mb-3 mt-5">
              <QESTextInput
                containerClassName="manage-templates-filters__input"
                label="Template Collection Name"
                testId="manage-templates-filter__manage-template-templateCollectionName"
                value={templateNameFilter}
                onKeyUp={(e) => handleSearchKeyUp(e)}
                onChange={(e) => setTemplateNameFilter(e.target.value)}
              />
              <QESButton qesType="primary" qesTestId="manage-templates__search-button" onClick={handleSearch}>Search</QESButton>
              <QESButton qesType="secondary" qesTestId="manage-templates__clear-filters" onClick={clearFilter} disabled={!!templateNameFilter}>Clear</QESButton>
            </div>
            <p>Displaying {numRecordsDisplayed} of {totalRecords} templates {!!templateNameFilter ? "(filtered)" : ""}</p>
            <div className="row mt-3">
              {uploadSuccess && uploadSuccess != undefined && (
                <QESAlert type={"success"} persist={false} onClose={() => { setUploadSuccess(undefined) }}>Upload succeeded!</QESAlert>
              )}
              {!uploadSuccess && uploadSuccess != undefined && (
                <QESAlert type={"danger"} persist={false} onClose={() => { setUploadSuccess(undefined) }}>Upload failed.</QESAlert>
                )}
            </div>
            <div className="row mt-3">
              <div className="col-12" style={{ display: "flex", flexDirection: "row", justifyContent: "flex-start" }} >
                <div style={{ minWidth: "50%", paddingRight: "1%"}}>
                  <Waffles<TemplateCollectionTableData>
                    columns={columnConfig}
                    customSort={customSortTemplateCollection}
                    data={templateCollections.slice(paginationProps.skip, paginationProps.skip + take)}
                    enablePagination={true}
                    fullWidth={true}
                    loading={loading}
                    passThroughSorting={false}
                    paginationProps={paginationProps}
                    rowDecorator={rowDecorator}
                    testId="client-manage-templateCollections-waffles-table" />
                </div>

                <div style={{ minWidth: "50%", paddingLeft: "1%" }}>
                  <Waffles<TemplateTableData>
                    columns={templateColumnConfig}
                    customSort={customSortTemplateList}
                    data={templateList}
                    enablePagination={true}
                    fullWidth={true}
                    loading={loading}
                    passThroughSorting={false}
                    paginationProps={paginationProps}
                    testId="template-manage-templates-waffles-table" />
                </div>
              </div>
            </div>
          </PageSection>
        </PageInner>
      </Page>
    </>
  );
}